import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Dropdown, Label } from '@heroui/react';
import { DataGrid, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import type { PaymentType, SalesTransaction } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatInLanguage } from '@/utils/dateLocale';
import { TransactionsSkeleton } from './TransactionsSkeleton';
import type { TransactionsFilterState } from './transactionsTypes';
import { TransactionsFilters } from './TransactionsFilters';
import { TransactionTotal } from './TransactionTotal';
import { TransactionDetailModal } from './TransactionDetailModal';
import {
    PAYMENT_TYPE_COLOR,
    PAYMENT_TYPE_ICON,
    PAYMENT_TYPE_LABEL_KEY,
    STATUS_BADGE_LABEL_KEY,
    TRANSACTION_COLUMN,
    TRANSACTION_ROW_ACTIONS,
    type TransactionRowAction,
    formatTransactionId,
    getCartUnitsCount,
    getDominantPaymentType,
    getStatusBadgeKind,
} from './transactionsMeta';

type TransactionsViewProps = TransactionsFilterState & {
    isLoading?: boolean;
    hasMore: boolean;
    isFetchingMore: boolean;
    items: SalesTransaction[];
    onLoadMore: () => void;
    totalCount: number;
};

const STATUS_BADGE_CLASS: Record<
    'done' | 'refund' | 'pending' | 'canceled',
    string
> = {
    canceled: 'bg-default/40 text-muted',
    done: 'bg-success/10 text-success-soft-foreground',
    pending: 'bg-warning/10 text-warning',
    refund: 'bg-danger/10 text-danger',
};

const STATUS_BADGE_ICON: Record<
    'done' | 'refund' | 'pending' | 'canceled',
    string
> = {
    canceled: 'solar:close-circle-bold',
    done: 'solar:check-circle-bold',
    pending: 'solar:clock-circle-bold',
    refund: 'solar:undo-left-round-square-bold',
};

const PAYMENT_ICON_BG: Record<PaymentType, string> = {
    Card: 'bg-accent/10',
    Cash: 'bg-success/10',
    Invoice: 'bg-warning/10',
    Voucher: 'bg-default/40',
};

const cartSummary = (transaction: SalesTransaction): string => {
    const names = transaction.cart.map((item) => item.name);
    if (names.length <= 3) {
        return names.join(', ');
    }
    return `${names.slice(0, 3).join(', ')} +${names.length - 3}`;
};

export const TransactionsView: React.FC<TransactionsViewProps> = ({
    discountedOnly,
    setDiscountedOnly,
    hasMore,
    isFetchingMore,
    isLoading = false,
    items,
    onLoadMore,
    search,
    setSearch,
    setTransactionType,
    transactionType,
}) => {
    const { t, i18n } = useTranslation('pages');
    const [openId, setOpenId] = React.useState<string | null>(null);

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.transactions.title'),
            tableAria: t('sales.widgets.transactions.tableAria'),
            actionsAria: t('sales.widgets.transactions.actionsAria'),
            columns: {
                txn: t('sales.widgets.transactions.columns.txn'),
                seller: t('sales.widgets.transactions.columns.seller'),
                status: t('sales.widgets.transactions.columns.status'),
                payment: t('sales.widgets.transactions.columns.payment'),
                total: t('sales.widgets.transactions.columns.total'),
            },
            empty: {
                title: t('sales.widgets.transactions.empty.title'),
            },
            unitsSuffix: t('sales.widgets.transactions.unitsSuffix'),
        }),
        [t],
    );

    const openTransaction = React.useMemo(
        () => items.find((tx) => tx.id === openId) ?? null,
        [items, openId],
    );

    // If the items list changes underneath (search refetch, page reset)
    // and the selected row is gone, drop the stale `openId` so the
    // modal can't re-open later if a row with the same id reappears.
    React.useEffect(() => {
        if (openId !== null && openTransaction === null) {
            setOpenId(null);
        }
    }, [openId, openTransaction]);

    const onRowAction = React.useCallback(
        (id: string, action: TransactionRowAction) => {
            if (action === 'details') {
                setOpenId(id);
            }
        },
        [],
    );

    const columns = React.useMemo<DataGridColumn<SalesTransaction>[]>(
        () => [
            {
                id: TRANSACTION_COLUMN.txn,
                header: copy.columns.txn,
                width: 130,
                isRowHeader: true,
                allowsSorting: true,
                sortFn: (a, b) =>
                    a.transactionDate.localeCompare(b.transactionDate),
                cell: (item) => (
                    <div
                        className="flex flex-col gap-0.5"
                        {...getNodeTestIdProp(
                            `transactions-row-txn-cell-${item.id}`,
                        )}
                    >
                        <span
                            className="font-mono text-xs font-bold text-accent"
                            {...getNodeTestIdProp(
                                `transactions-row-id-${item.id}`,
                            )}
                        >
                            {formatTransactionId(item.id)}
                        </span>
                        <span
                            className="text-[11px] text-muted"
                            {...getNodeTestIdProp(
                                `transactions-row-date-${item.id}`,
                            )}
                        >
                            {formatInLanguage(
                                item.transactionDate,
                                'D MMM · HH:mm',
                                i18n.language,
                            )}
                        </span>
                    </div>
                ),
            },
            {
                id: TRANSACTION_COLUMN.seller,
                header: copy.columns.seller,
                allowsSorting: true,
                sortFn: (a, b) => a.seller.name.localeCompare(b.seller.name),
                cell: (item) => (
                    <div
                        className="flex min-w-0 flex-col gap-0.5"
                        {...getNodeTestIdProp(
                            `transactions-row-seller-cell-${item.id}`,
                        )}
                    >
                        <div
                            className="flex items-center gap-1.5"
                            {...getNodeTestIdProp(
                                `transactions-row-seller-line-${item.id}`,
                            )}
                        >
                            <Icon
                                aria-hidden
                                className="size-2.5 text-muted"
                                icon="solar:user-bold-duotone"
                                {...getNodeTestIdProp(
                                    `transactions-row-seller-icon-${item.id}`,
                                )}
                            />
                            <span
                                className="truncate text-xs font-medium text-foreground"
                                {...getNodeTestIdProp(
                                    `transactions-row-seller-${item.id}`,
                                )}
                            >
                                {item.seller.name}
                            </span>
                        </div>
                        <span
                            className="truncate text-[11px] text-muted"
                            {...getNodeTestIdProp(
                                `transactions-row-summary-${item.id}`,
                            )}
                        >
                            {cartSummary(item)}
                        </span>
                    </div>
                ),
            },
            {
                id: TRANSACTION_COLUMN.status,
                header: copy.columns.status,
                width: 110,
                allowsSorting: true,
                // Sort by the *displayed* badge kind, not the raw `status`:
                // a refunded row shows the "Refund" badge regardless of its
                // underlying status, so it must sort with the other refunds.
                sortFn: (a, b) =>
                    getStatusBadgeKind(a).localeCompare(getStatusBadgeKind(b)),
                cell: (item) => {
                    const kind = getStatusBadgeKind(item);
                    return (
                        <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_BADGE_CLASS[kind]}`}
                            {...getNodeTestIdProp(
                                `transactions-row-status-${item.id}`,
                            )}
                        >
                            <Icon
                                aria-hidden
                                className="size-3"
                                icon={STATUS_BADGE_ICON[kind]}
                                {...getNodeTestIdProp(
                                    `transactions-row-status-icon-${item.id}`,
                                )}
                            />
                            {t(STATUS_BADGE_LABEL_KEY[kind])}
                        </span>
                    );
                },
            },
            {
                id: TRANSACTION_COLUMN.payment,
                header: copy.columns.payment,
                width: 140,
                allowsSorting: true,
                sortFn: (a, b) =>
                    (getDominantPaymentType(a) ?? '').localeCompare(
                        getDominantPaymentType(b) ?? '',
                    ),
                cell: (item) => {
                    const dominant = getDominantPaymentType(item);
                    if (!dominant) {
                        return null;
                    }
                    return (
                        <div
                            className="flex items-center gap-1.5"
                            {...getNodeTestIdProp(
                                `transactions-row-payment-cell-${item.id}`,
                            )}
                        >
                            <div
                                aria-hidden
                                className={`flex size-6 shrink-0 items-center justify-center rounded-lg ${PAYMENT_ICON_BG[dominant]} ${PAYMENT_TYPE_COLOR[dominant]}`}
                                {...getNodeTestIdProp(
                                    `transactions-row-payment-icon-wrap-${item.id}`,
                                )}
                            >
                                <Icon
                                    className="size-3"
                                    icon={PAYMENT_TYPE_ICON[dominant]}
                                    {...getNodeTestIdProp(
                                        `transactions-row-payment-icon-${item.id}`,
                                    )}
                                />
                            </div>
                            <div
                                className="flex flex-col"
                                {...getNodeTestIdProp(
                                    `transactions-row-payment-stack-${item.id}`,
                                )}
                            >
                                <span
                                    className={`text-[11px] font-medium leading-none ${PAYMENT_TYPE_COLOR[dominant]}`}
                                    {...getNodeTestIdProp(
                                        `transactions-row-payment-${item.id}`,
                                    )}
                                >
                                    {t(PAYMENT_TYPE_LABEL_KEY[dominant])}
                                </span>
                                <span
                                    className="mt-0.5 text-[10px] leading-none text-muted"
                                    {...getNodeTestIdProp(
                                        `transactions-row-units-${item.id}`,
                                    )}
                                >
                                    {`${getCartUnitsCount(item)} ${copy.unitsSuffix}`}
                                </span>
                            </div>
                        </div>
                    );
                },
            },
            {
                id: TRANSACTION_COLUMN.total,
                header: copy.columns.total,
                width: 180,
                align: 'end',
                accessorKey: 'total',
                allowsSorting: true,
                cell: (item) => <TransactionTotal transaction={item} />,
            },
            {
                id: TRANSACTION_COLUMN.actions,
                header: '',
                pinned: 'end',
                width: 40,
                align: 'center',
                cellClassName: 'bg-surface',
                headerClassName: 'bg-surface-secondary',
                cell: (item) => (
                    <div
                        className="flex justify-center"
                        {...getNodeTestIdProp(
                            `transactions-row-actions-${item.id}`,
                        )}
                    >
                        <Dropdown>
                            <Button
                                aria-label={copy.actionsAria}
                                isIconOnly
                                size="sm"
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    `transactions-row-actions-trigger-${item.id}`,
                                )}
                            >
                                <Icon icon="gravity-ui:ellipsis-vertical" />
                            </Button>
                            <Dropdown.Popover className="min-w-[160px]">
                                <Dropdown.Menu
                                    onAction={(key) =>
                                        onRowAction(
                                            item.id,
                                            key as TransactionRowAction,
                                        )
                                    }
                                >
                                    {TRANSACTION_ROW_ACTIONS.map((action) => (
                                        <Dropdown.Item
                                            id={action.id}
                                            key={action.id}
                                            textValue={t(action.labelKey)}
                                            variant={action.variant}
                                            {...getNodeTestIdProp(
                                                `transactions-row-action-${action.id}-${item.id}`,
                                            )}
                                        >
                                            <Icon icon={action.icon} />
                                            <Label>{t(action.labelKey)}</Label>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown>
                    </div>
                ),
            },
        ],
        [copy, i18n.language, onRowAction, t],
    );

    return (
        <section
            aria-busy={isLoading || undefined}
            aria-labelledby="transactions-widget-title"
            {...getNodeTestIdProp('transactions-section')}
        >
            <Widget
                className="flex flex-col gap-0 overflow-hidden p-0"
                {...getNodeTestIdProp('transactions-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('transactions-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('transactions-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"
                            {...getNodeTestIdProp('transactions-icon-wrap')}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:bill-list-bold-duotone"
                                {...getNodeTestIdProp('transactions-icon')}
                            />
                        </div>
                        {copy.title}
                    </Widget.Title>
                </Widget.Header>
                <TransactionsFilters
                    discountedOnly={discountedOnly}
                    setDiscountedOnly={setDiscountedOnly}
                    search={search}
                    setSearch={setSearch}
                    transactionType={transactionType}
                    setTransactionType={setTransactionType}
                />
                <DataGrid<SalesTransaction>
                    aria-label={copy.tableAria}
                    columns={columns}
                    data={items}
                    defaultSortDescriptor={{
                        column: TRANSACTION_COLUMN.txn,
                        direction: 'descending',
                    }}
                    getRowId={(item) => item.id}
                    isLoadingMore={isFetchingMore}
                    onLoadMore={hasMore ? onLoadMore : undefined}
                    renderEmptyState={() =>
                        isLoading ? (
                            <TransactionsSkeleton />
                        ) : (
                            <div
                                className="flex flex-col items-center gap-3 py-12 text-muted"
                                {...getNodeTestIdProp('transactions-empty')}
                            >
                                <Icon
                                    aria-hidden
                                    className="size-8 text-default-500"
                                    icon="solar:bill-list-bold-duotone"
                                    {...getNodeTestIdProp(
                                        'transactions-empty-icon',
                                    )}
                                />
                                <span
                                    className="text-sm"
                                    {...getNodeTestIdProp(
                                        'transactions-empty-title',
                                    )}
                                >
                                    {copy.empty.title}
                                </span>
                            </div>
                        )
                    }
                    scrollContainerClassName="max-h-[520px] overflow-y-auto"
                    variant="primary"
                    {...getNodeTestIdProp('transactions-grid')}
                />
                <TransactionDetailModal
                    isOpen={openTransaction !== null}
                    onClose={() => setOpenId(null)}
                    transaction={openTransaction}
                />
            </Widget>
        </section>
    );
};
